import { useState } from 'react';

import { AppShell } from '@/components/layout/AppShell';
import { Button, Card, PageContainer, PageHeader, Switch, useToast } from '@/components/ui';
import { NotificationsHistorySection } from './NotificationsHistorySection';
// R6.5 / FRENTE E: card de gestão de push (ativa/desativa device atual).
import { PushNotificationsCard } from './PushNotificationsCard';
import { useAuth } from '@/contexts/AuthContext';
import {
  fireConfetti,
  isCelebracoesEnabled,
  setCelebracoesEnabled,
} from '@/lib/celebrations';
import {
  EVENT_TYPES,
  CHANNELS,
  useNotificationPreferences,
  useNotificationPreferenceActions,
  isChannelEnabled,
  type NotificationChannel,
  type NotificationEventType,
} from '@/lib/notification-preferences';

/**
 * /perfil/notificacoes — o que você recebe e por onde.
 *
 * Regra 8.1: o aviso dentro do app é SEMPRE ligado (caixa desabilitada).
 *
 * **FASE-C — o que MORREU nesta tela:**
 *   - O aviso "Modo R0: integração real com Email/WhatsApp/Push chega em R7…
 *     stubs que logam no console do navegador" — jargão de handoff e nome de
 *     rodada na cara do usuário.
 *   - As colunas **Email** e **WhatsApp**: no servidor esses dois canais só
 *     escrevem uma linha de log (`notifications-sender.ts`), nada sai. Ligar
 *     o interruptor não fazia nada. Ficaram só os dois que funcionam de
 *     verdade: no app e no celular (push nativo, quando o servidor tem VAPID).
 *     A preferência de e-mail continua guardada no banco — quando o envio
 *     existir, a coluna volta com o dado que já está lá.
 *   - O botão "Salvar", que o próprio texto abaixo dele admitia ser cosmético
 *     (só mostrava um toast; a gravação sempre foi automática).
 */

// FASE-C: canais que realmente entregam. Email/WhatsApp voltam quando o
// servidor souber enviar — a preferência salva não é perdida enquanto isso.
const CANAIS_ATIVOS = CHANNELS.filter(
  (c) => c.slug === 'in-app' || c.slug === 'push',
);

export function NotificationsPage() {
  const { user } = useAuth();
  const userId = user?.id ?? 'anonymous';
  void useNotificationPreferences(userId); // assina mudanças pro re-render
  // TD-V2-UX-01 (2026-05-26): copyFrom movido pra /admin/usuarios. Aqui só
  // setPreference + setAllForChannel.
  const { setPreference, setAllForChannel } =
    useNotificationPreferenceActions(userId);
  const [bulkPending, setBulkPending] = useState<NotificationChannel | null>(null);
  // UI-5: toggle local pra confete (localStorage de UX, não de negócio).
  const [celebracoesOn, setCelebracoesOn] = useState(() => isCelebracoesEnabled());

  // FASE 2 / M2 (auditoria 2026-05-21): toast provider global em vez de
  // setTimeout inline. Duração canônica 3s.
  const { show: showToast } = useToast();

  function handleToggle(eventType: NotificationEventType, channel: NotificationChannel) {
    if (channel === 'in-app') return; // sempre on
    const current = isChannelEnabled(userId, eventType, channel);
    setPreference(eventType, channel, !current);
    showToast('Preferências salvas');
  }

  async function handleBulkSet(channel: NotificationChannel, enabled: boolean) {
    if (channel === 'in-app') return; // protegido
    setBulkPending(channel);
    try {
      await setAllForChannel(channel, enabled);
      showToast(
        `${enabled ? 'Ativado' : 'Desativado'}: ${CHANNELS.find((c) => c.slug === channel)?.label} em todos os eventos`,
      );
    } finally {
      setBulkPending(null);
    }
  }

  if (!user) {
    return (
      <AppShell>
        {/* LAYOUT-CANONICO: os dois wrappers desta tela (aviso de login e
            conteúdo) viraram a régua única; o `text-center` é layout interno e
            ficou no div de dentro. */}
        <PageContainer>
          <div className="text-center">
            <p className="text-sm text-ink-2 dark:text-ink-dark-2">
              Faça login pra acessar suas preferências.
            </p>
          </div>
        </PageContainer>
      </AppShell>
    );
  }

  // Grid da matriz: rótulo do evento + uma coluna por canal ativo. No mobile
  // vira empilhado (era aqui o estouro de +89px do diagnóstico).
  const gridCols = `minmax(0,1fr) repeat(${CANAIS_ATIVOS.length}, 6rem)`;

  return (
    <AppShell>
      <PageContainer>
        <PageHeader
          crumb="Perfil"
          title="Notificações"
          subtitle="Escolha o que você quer receber e por onde. O aviso dentro do app é sempre ligado — assim nada importante passa batido quando você entrar."
        />

        {/* R6.5 / FRENTE E: gestão de push native do device atual */}
        <PushNotificationsCard />

        <Card variant="flat" className="overflow-hidden">
          <div className="border-b border-line dark:border-line-dark px-4 py-3">
            <h2 className="text-sm font-semibold text-ink-1 dark:text-ink-dark-1">
              O que você recebe
            </h2>
            <p className="mt-1 text-xs text-ink-2 dark:text-ink-dark-2">
              Cada mudança é salva na hora. Aviso por e-mail e WhatsApp chega
              quando essas integrações estiverem ligadas.
            </p>
          </div>

          {/* Cabeçalho das colunas — só no desktop (no mobile cada opção tem
              o nome do canal ao lado do interruptor). */}
          <div
            className="hidden items-center gap-3 border-b border-line bg-paper-warm px-4 py-2 text-xs font-semibold uppercase tracking-wider text-ink-3 dark:border-line-dark dark:bg-paper-dark-warm dark:text-ink-dark-3 sm:grid"
            style={{ gridTemplateColumns: gridCols }}
          >
            <div>Aviso</div>
            {CANAIS_ATIVOS.map((c) => (
              <div key={c.slug} className="text-center">
                {c.label}
              </div>
            ))}
          </div>

          {/* UI-4 / TD-V2-NOTIF-02: aplicar a todos os eventos de uma vez */}
          <div
            data-testid="notif-bulk-row"
            className="flex flex-wrap items-center gap-3 border-b border-line bg-paper-warm/40 px-4 py-2 dark:border-line-dark dark:bg-paper-dark-warm/40 sm:grid sm:gap-3"
            style={{ gridTemplateColumns: gridCols }}
          >
            <div className="text-xs text-ink-2 dark:text-ink-dark-2">
              Aplicar a todos os avisos
            </div>
            {CANAIS_ATIVOS.map((c) => {
              if (c.alwaysOn) {
                return (
                  <div
                    key={c.slug}
                    className="text-center text-xs text-ink-3 dark:text-ink-dark-3"
                  >
                    <span className="sm:hidden">{c.label}: </span>sempre ligado
                  </div>
                );
              }
              return (
                <div key={c.slug} className="flex justify-center gap-1">
                  <button
                    type="button"
                    onClick={() => handleBulkSet(c.slug, true)}
                    disabled={bulkPending !== null}
                    data-testid={`notif-bulk-on-${c.slug}`}
                    aria-label={`Ativar ${c.label} em todos os avisos`}
                    className="rounded border border-line px-2 py-0.5 text-xs text-ink-1 hover:bg-red-soft/30 disabled:opacity-50 dark:border-line-dark dark:text-ink-dark-1"
                  >
                    Todos
                  </button>
                  <button
                    type="button"
                    onClick={() => handleBulkSet(c.slug, false)}
                    disabled={bulkPending !== null}
                    data-testid={`notif-bulk-off-${c.slug}`}
                    aria-label={`Desativar ${c.label} em todos os avisos`}
                    className="rounded border border-line px-2 py-0.5 text-xs text-ink-1 hover:bg-red-soft/30 disabled:opacity-50 dark:border-line-dark dark:text-ink-dark-1"
                  >
                    Nenhum
                  </button>
                </div>
              );
            })}
          </div>

          {EVENT_TYPES.map((evt) => (
            <div
              key={evt.slug}
              data-testid={`notif-row-${evt.slug}`}
              className="border-b border-line px-4 py-3 last:border-b-0 dark:border-line-dark sm:grid sm:items-center sm:gap-3"
              style={{ gridTemplateColumns: gridCols }}
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
                  {evt.label}
                </p>
                {/* A explicação fica sempre visível (antes era um "ⓘ" que
                    escondia o mesmo texto num balão nativo). */}
                <p
                  data-testid={`notif-tooltip-${evt.slug}`}
                  title={evt.description}
                  className="mt-0.5 text-xs text-ink-2 dark:text-ink-dark-2"
                >
                  {evt.description}
                </p>
              </div>
              <div className="mt-2 flex flex-wrap gap-x-6 gap-y-2 sm:mt-0 sm:contents">
                {CANAIS_ATIVOS.map((c) => {
                  const checked = isChannelEnabled(userId, evt.slug, c.slug);
                  const disabled = c.alwaysOn;
                  return (
                    <label
                      key={c.slug}
                      className="flex items-center gap-2 sm:justify-center"
                    >
                      <input
                        type="checkbox"
                        data-testid={`notif-checkbox-${evt.slug}-${c.slug}`}
                        checked={checked}
                        disabled={disabled}
                        onChange={() => handleToggle(evt.slug, c.slug)}
                        aria-label={`${c.label} para ${evt.label}`}
                        className="h-4 w-4 rounded accent-red text-red focus:ring-red disabled:cursor-not-allowed disabled:opacity-50"
                      />
                      <span className="text-xs text-ink-2 dark:text-ink-dark-2 sm:hidden">
                        {c.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </Card>

        {/* UI-5 / TD-V2-AUTO-21: toggle confete + botão de teste */}
        <Card variant="flat" className="mt-4 p-4" data-testid="celebracoes-panel">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
                Comemorar metas batidas
              </p>
              <p className="mt-0.5 text-xs text-ink-2 dark:text-ink-dark-2">
                Solta confete na tela quando um marco é alcançado. O aviso da
                conquista aparece de qualquer jeito.
              </p>
            </div>
            <div className="flex items-center gap-3 sm:flex-shrink-0">
              <Switch
                checked={celebracoesOn}
                onChange={(e) => {
                  const v = e.target.checked;
                  setCelebracoesOn(v);
                  setCelebracoesEnabled(v);
                  showToast(v ? 'Confete ativado' : 'Confete desativado');
                }}
                label="Mostrar confete"
                data-testid="celebracoes-toggle"
              />
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => fireConfetti()}
                data-testid="celebracoes-test"
                disabled={!celebracoesOn}
              >
                Ver como fica
              </Button>
            </div>
          </div>
        </Card>

        {/* FEAT-V2-028: histórico paginado abaixo da matriz */}
        <NotificationsHistorySection userId={userId} />
      </PageContainer>
    </AppShell>
  );
}
